import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { SearchGroup } from '@components/input';
import {
    bonificationSelector,
    filterItems,
    IFilterSelectItems,
    orderItemsSelector,
} from '@redux/slices/order';
import { Col, Row } from 'antd';
import React, { memo } from 'react';
import { exportItemsColumns, filterOptionsTranspo } from '../constants';
import { SelectItemsWarning } from './select-items-warning';
import { useDispatch, useSelector } from 'react-redux';
import { ExportButton } from '@styles/components/button';
import { utils, writeFile } from 'xlsx';
import '@extensions/number';
import moment from 'moment';
import { SelectCampaign } from './select-campaigns';
import { CampaignQuotaButton } from './campaign-quota-button';
import { selectedCampaignsSelector } from '@redux/slices/campaign';
import { translate } from '@components/i18n';
import { formatMoney } from '@utils/money';
import {
    BudgetPreviewItem,
    BudgetPreviewSection,
    openBudgetPreview,
} from '@utils/budget-pdf';
import styled from 'styled-components';
import { ItemResponse } from '@models/order/items/response';

interface SelectItemsHeaderProps {
    readOnly?: boolean;
    marketing?: boolean;
    bonification?: boolean;
}

const formatItemNumber = (value: number | string | undefined): string => {
    if (value === undefined || value === null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('pt-BR');
};

export const SelectItemsHeaderFooter: React.FC<SelectItemsHeaderProps> = memo(
    ({ bonification = false }) => {
        const dispatch = useDispatch();
        const selectItemsState = useSelector(orderItemsSelector);
        const { selectedOrders } = useSelector(bonificationSelector);
        const selectedCampaigns = useSelector(selectedCampaignsSelector);
        const reachQuotaCampaigns = selectedCampaigns.filter(
            (c) => c.tipo === 'DESCONTO_ATINGIR_META',
        );

        const filter = (filter: Partial<IFilterSelectItems>): void => {
            dispatch(filterItems(filter));
        };

        function exportToExcel(): void {
            let htmls = `<table id="tbl_exporttable_to_xls" border="1">
            <thead>
                <th>${translate('columns.date')}: ${moment().format('DD-MM-YYYY')}</th>
                <th>${translate('columns.branch')}: ${selectItemsState.order.orderType.platform.description
                }</th>
            </thead>
             <thead>
                <th>${translate('columns.code')}: ${selectItemsState.order.customer.code
                }</th>
                <th>${translate('columns.client')}: ${selectItemsState.order.customer.name
                }</th>
            </thead>
            <thead>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th>${translate('columns.orderAmount')}:</th>
              <th>${formatMoney(selectItemsState?.selectedMoeda?.simbolo, selectItemsState.order.valorPedido)}</th>
            </thead>
            <thead>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
            </thead>
            <thead>
                <th>Itens</th>
                <th>${translate('columns.code')}</th>
                <th>${translate('columns.presentation')}</th>
                <th>${translate('columns.qtd')}</th>
                <th>${translate('columns.presentationQtd')}</th>
                <th>${translate('columns.amountQtd')}</th>
                <th>${translate('columns.price')}</th>
                <th>${translate('columns.promo')} %</th>
                <th>${translate('columns.desc')} %</th>
                <th>${translate('columns.subTotal')} %</th>
            </thead><tbody>`;

            const items = exportItemsColumns(selectItemsState.availableItems);

            for (const item of items) {
                htmls += `<tr>
                            <td>${item.Item}</td>
                            <td>${item.Cod}</td>
                            <td>${item.Apresentacao}</td>
                            <td>${item.Quantity}</td>
                            <td>${item.QtdApres}</td>
                            <td>${item.QtdTotal}</td>
                            <td>${item.Preco}</td>
                            <td>${item.Promo}</td>
                            <td>${item.Desconto}</td>
                            <td>${item.Subtotal}</td>
                        </tr>`;
            }

            htmls += `</tbody></table>`;

            document.getElementById('export-div').innerHTML = htmls;

            const fileName = selectItemsState.draftRef
                ? `${translate('general.saleOrder')} - ${selectItemsState.draftRef
                }`
                : translate('general.saleOrder');

            const elt = document.getElementById('tbl_exporttable_to_xls');
            const wb = utils.table_to_book(elt);

            writeFile(wb, `${fileName}.` + 'xlsx');
        }

        function exportToBudgetPdf(): void {
            const order = selectItemsState.order;
            const customer = order?.customer;
            const moedaSimbolo = selectItemsState?.selectedMoeda?.simbolo;

            const mapItems = (
                source: Partial<ItemResponse>[] | undefined,
            ): BudgetPreviewItem[] =>
                (source ?? [])
                    .filter(
                        (i) =>
                            i.quantity > 0 && i.custo > 0 && i.precoBase > 0,
                    )
                    .map((i) => ({
                        description: i.description ?? '',
                        code: i.code ?? '',
                        quantity: formatItemNumber(i.quantity),
                        quantityPresentation: formatItemNumber(
                            i.quantityPresentation,
                        ),
                        totalQuantity: formatItemNumber(i.totalQuantity),
                        price: formatMoney(moedaSimbolo, i.costModifiable),
                        subtotal: formatMoney(moedaSimbolo, i.total),
                        discount: i.discount,
                    }));

            const sections: BudgetPreviewSection[] = [];

            sections.push({
                title: bonification
                    ? translate('columns.bonification')
                    : translate('general.sale'),
                items: mapItems(selectItemsState.availableItems),
                totalFormatted: formatMoney(
                    moedaSimbolo,
                    order?.valorPedido ?? 0,
                ),
                isBonification: bonification,
            });

            if (bonification && selectedOrders?.length > 0) {
                const seenKeys = new Set<string>();
                for (const linked of selectedOrders) {
                    const linkedKey = linked.key ? String(linked.key) : '';
                    if (linkedKey && seenKeys.has(linkedKey)) continue;
                    if (linkedKey) seenKeys.add(linkedKey);

                    const items = mapItems(linked.availableItems);
                    if (items.length === 0) continue;

                    const codeSuffix = linkedKey ? ` #${linkedKey}` : '';
                    sections.push({
                        title: `${translate('general.sale')}${codeSuffix}`,
                        items,
                        totalFormatted: formatMoney(
                            moedaSimbolo,
                            linked.valorPedido ?? 0,
                        ),
                        isBonification: false,
                    });
                }
            }

            const address = customer?.address?.trim()
                ? customer.address
                : customer?.alternativeAddress?.trim()
                    ? customer.alternativeAddress
                    : '';

            const cityState = [customer?.city, customer?.state]
                .filter(Boolean)
                .join(' - ');
            const fullAddress = [address, cityState]
                .filter(Boolean)
                .join(', ');

            const typeLabel = customer?.typeId
                ? `${customer.typeId}${customer.type ? ` - ${customer.type}` : ''}`
                : customer?.type ?? '';

            // Condição de pagamento no orçamento:
            // - Venda sem bonificação: exibir condição de pagamento da venda
            // - Venda com bonificação comercial: exibir condição de pagamento da venda (não da bonificação)
            // - Apenas bonificação comercial: não exibir
            let paymentConditionLabel = '';
            if (bonification) {
                // Tela de bonificação: pegar a condição de pagamento da venda vinculada
                const saleOrder = selectedOrders?.length > 0 ? selectedOrders[0] : null;
                if (saleOrder?.paymentCondition?.id) {
                    paymentConditionLabel = `${saleOrder.paymentCondition.id}${saleOrder.paymentCondition.description ? ` - ${saleOrder.paymentCondition.description}` : ''}`;
                }
            } else {
                // Tela de venda: usar a condição de pagamento da venda
                paymentConditionLabel = order?.paymentCondition?.id
                    ? `${order.paymentCondition.id}${order.paymentCondition.description ? ` - ${order.paymentCondition.description}` : ''}`
                    : order?.paymentCondition?.description ?? '';
            }

            openBudgetPreview({
                date: moment().format('DD/MM/YYYY'),
                orderCode: selectItemsState.draftRef
                    ? String(selectItemsState.draftRef)
                    : '',
                sections,
                logoUrl: `${window.location.origin}/images/logo.png`,
                customer: {
                    name: customer?.name ?? '',
                    cnpj: customer?.cnpj ?? '',
                    typeLabel,
                    branch: order?.orderType?.platform?.description ?? '',
                    address: fullAddress,
                    paymentCondition: paymentConditionLabel,
                },
                labels: {
                    documentTitle: translate('general.budgetDocument'),
                    date: translate('columns.date'),
                    orderCode: translate('columns.code'),
                    client: translate('columns.client'),
                    cnpj: translate('columns.cnpj'),
                    clientType: translate('columns.clientType'),
                    branch: translate('columns.branch'),
                    address: translate('columns.address'),
                    paymentCondition: translate('columns.paymentCondition'),
                    items: translate('columns.items'),
                    code: translate('columns.code'),
                    qtd: translate('columns.qtd'),
                    presentationQtd: translate('columns.presentationQtd'),
                    amountQtd: translate('columns.amountQtd'),
                    price: translate('columns.price'),
                    subTotal: translate('columns.subTotal'),
                    orderAmount: translate('columns.orderAmount'),
                    desc: translate('columns.desc'),
                },
            });
        }

        return (
            <>
                <Row style={{ marginTop: 20, paddingLeft: 16, paddingRight: 16, }} align="middle" justify='center'>
                    <Col xs={24}>
                        <Row justify={'space-between'} align="middle">
                            <Col xs={24} xl={8} style={{ marginTop: 8 }}>
                                <SearchGroup
                                    onChangeSelect={(v) =>
                                        filter({ filterBy: v })
                                    }
                                    options={filterOptionsTranspo}
                                    allowClear
                                    placeholder={translate(
                                        'forms.placeholders.search',
                                    )}
                                    onSearch={(e) => {
                                        filter({ filterValue: e });
                                    }}
                                    onChange={(e) => {
                                        filter({
                                            filterValue: e.target.value,
                                        });
                                    }}
                                />
                            </Col>
                            <Col xs={24} xl={16} style={{ marginTop: 8 }}>
                                <RowStyled justify='end'>
                                    <Col flex={1} xl={16}>
                                        <SelectItemsWarning />
                                    </Col>
                                    {reachQuotaCampaigns.map((c) => (
                                        <Col key={`quota-${c.id}`}>
                                            <CampaignQuotaButton campaign={c} />
                                        </Col>
                                    ))}
                                    <Col>
                                        <ExportButton
                                            style={{ marginLeft: 0 }}
                                            onClick={exportToExcel}
                                        >
                                            {translate('general.export')}{' '}
                                            <DownloadOutlined />
                                        </ExportButton>
                                    </Col>
                                    <Col>
                                        <BudgetPreviewButton
                                            style={{ marginLeft: 0 }}
                                            onClick={exportToBudgetPdf}
                                        >
                                            {translate('general.budgetPreview')}{' '}
                                            <FilePdfOutlined />
                                        </BudgetPreviewButton>
                                    </Col>
                                    <SelectCampaign bonification={bonification} />
                                </RowStyled>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <div
                    style={{
                        visibility: 'hidden',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                    }}
                    id={'export-div'}
                ></div>
            </>
        );
    },
);

const RowStyled = styled(Row)`
    gap: 8px;
`

const BudgetPreviewButton = styled(ExportButton)`
    background: #013a93;

    :hover,
    :focus,
    :active {
        background: #012a6b !important;
    }
`